import { deiterateAPI } from '@/js/external-js/variableModules/utils/deiterateAPI.util';

export interface WhiteLabelConfig {
  id: string;
  domain: string;
  appName: string;
  logoLight?: string;
  logoDark?: string;
  favicon?: string;
  sidebarLogoSize?: 'small' | 'medium' | 'large' | 'hidden';
  sidebarLogoHeight?: number;
  primaryColor: string;
  primaryForeground?: string;
  secondaryColor?: string;
  accentColor?: string;
  sidebarBackground?: string;
  headerBarColor?: string;
  navBarBackground?: string;
  navBarText?: string;
  navBarBorder?: string;
  fontFamily?: string;
  fontUrl?: string;
  customCSS?: string;
  sidebarNavigation?: {
    items: Array<{
      id: string;
      visible: boolean;
      order: number;
    }>;
  };
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

const CONFIG_CACHE_KEY = 'whitelabel_config';
const CONFIG_CACHE_TTL = 5 * 60 * 1000; // 5 minutes

interface CachedConfig {
  config: WhiteLabelConfig | null;
  timestamp: number;
  domain: string;
}

function getCachedConfig(): CachedConfig | null {
  try {
    const cached = localStorage.getItem(CONFIG_CACHE_KEY);
    if (cached) {
      return JSON.parse(cached);
    }
  } catch (e) {
    console.error('[WhiteLabel] Failed to read cache:', e);
  }
  return null;
}

function setCachedConfig(config: WhiteLabelConfig | null, domain: string): void {
  try {
    const cacheEntry: CachedConfig = {
      config,
      timestamp: Date.now(),
      domain,
    };
    localStorage.setItem(CONFIG_CACHE_KEY, JSON.stringify(cacheEntry));
  } catch (e) {
    console.error('[WhiteLabel] Failed to write cache:', e);
  }
}

export async function fetchWhiteLabelConfig(domain?: string): Promise<WhiteLabelConfig | null> {
  const currentDomain = domain || window.location.hostname;
  
  // Check cache first
  const cached = getCachedConfig();
  if (cached && cached.domain === currentDomain && Date.now() - cached.timestamp < CONFIG_CACHE_TTL) {
    console.log('[WhiteLabel] Using cached config for:', currentDomain);
    return cached.config;
  }

  try {
    // Fetch from Thalassa API using deiterateAPI
    const config = await deiterateAPI.GET<WhiteLabelConfig>(`/v1/whitelabel/${encodeURIComponent(currentDomain)}`, {
      backend: 'thalassa',
      headers: {
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache',
        'Expires': '0',
      },
    }, false); // requireAuth: false
    
    setCachedConfig(config, currentDomain);
    return config;
  } catch (error) {
    if (error instanceof Error && error.message.includes('404')) {
      console.log('[WhiteLabel] No config found for domain:', currentDomain);
      setCachedConfig(null, currentDomain);
      return null;
    }
    
    console.error('[WhiteLabel] Failed to fetch config:', error);
    // Return cached config even if expired, as fallback
    if (cached && cached.domain === currentDomain) {
      return cached.config;
    }
    return null;
  }
}

function getThalassaApiBase(): string {
  // Use the existing deiterateAPI utility to get Thalassa base URL
  // This respects environment variables and configuration
  return (import.meta.env.VITE_THALASSA_API || import.meta.env.VITE_THALASSA_URL) as string;
}

export function applyWhiteLabelConfig(config: WhiteLabelConfig): void {
  console.log('[WhiteLabel] Applying config:', config.appName);
  
  // Apply CSS variables
  const root = document.documentElement;
  
  if (config.primaryColor) {
    root.style.setProperty('--primary', config.primaryColor);
  }
  if (config.primaryForeground) {
    root.style.setProperty('--primary-foreground', config.primaryForeground);
  }
  if (config.secondaryColor) {
    root.style.setProperty('--secondary', config.secondaryColor);
  }
  if (config.accentColor) {
    root.style.setProperty('--accent', config.accentColor);
  }
  if (config.sidebarBackground) {
    root.style.setProperty('--sidebar', config.sidebarBackground);
  }
  if (config.headerBarColor) {
    root.style.setProperty('--header-bar-color', config.headerBarColor);
  }
  if (config.navBarBackground) {
    root.style.setProperty('--nav-bar-background', config.navBarBackground);
  }
  if (config.navBarText) {
    root.style.setProperty('--nav-bar-text', config.navBarText);
  }
  if (config.navBarBorder) {
    root.style.setProperty('--nav-bar-border', config.navBarBorder);
  }
  if (config.headerBarColor) {
    root.style.setProperty('--accent-bar', config.headerBarColor);
  }
  
  // Apply favicon
  if (config.favicon) {
    updateFavicon(config.favicon);
  }
  
  // Apply custom font
  if (config.fontUrl && config.fontFamily) {
    loadCustomFont(config.fontUrl, config.fontFamily);
  }
  
  // Update document title with app name
  if (config.appName) {
    const currentTitle = document.title;
    // Replace "de.iterate" or similar with custom app name
    if (currentTitle.includes('de.iterate') || currentTitle.includes('De.iterate')) {
      document.title = currentTitle.replace(/de\.iterate|De\.iterate/gi, config.appName);
    }
  }
  
  // Apply custom CSS if provided
  if (config.customCSS) {
    applyCustomCSS(config.customCSS);
  }
}

function applyCustomCSS(css: string): void {
  // Remove existing custom CSS if any
  const existingStyle = document.getElementById('whitelabel-custom-css');
  if (existingStyle) {
    existingStyle.remove();
  }
  
  // Add new custom CSS
  const style = document.createElement('style');
  style.id = 'whitelabel-custom-css';
  style.textContent = css;
  document.head.appendChild(style);
  
  console.log('[WhiteLabel] Applied custom CSS');
}

function updateFavicon(faviconUrl: string): void {
  // Remove existing favicons
  const existingFavicons = document.querySelectorAll('link[rel*="icon"]');
  existingFavicons.forEach(el => el.remove());
  
  // Add new favicon
  const link = document.createElement('link');
  link.rel = 'icon';
  link.href = faviconUrl;
  document.head.appendChild(link);
  
  // Also add apple-touch-icon
  const appleLink = document.createElement('link');
  appleLink.rel = 'apple-touch-icon';
  appleLink.href = faviconUrl;
  document.head.appendChild(appleLink);
}

function loadCustomFont(fontUrl: string, fontFamily: string): void {
  // Check if already loaded
  if (document.querySelector(`link[href="${fontUrl}"]`)) {
    return;
  }
  
  // Load font stylesheet
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = fontUrl;
  document.head.appendChild(link);
  
  // Apply font family
  document.documentElement.style.setProperty('--font-family', fontFamily);
  document.body.style.fontFamily = fontFamily;
}

export function getWhiteLabelLogo(config: WhiteLabelConfig | null, isDark: boolean): string | null {
  if (!config) return null;
  return isDark ? (config.logoDark || config.logoLight || null) : (config.logoLight || null);
}

// Fetch all available white-label configs (for dev picker)

export async function fetchAllWhiteLabelConfigs(): Promise<WhiteLabelConfig[]> {
  try {
    const data = await deiterateAPI.GET<{ configs: WhiteLabelConfig[] }>('/v1/whitelabel', {
      backend: 'thalassa',
      headers: {
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache',
        'Expires': '0',
      },
    }, false); // requireAuth: false
    
    return data.configs || [];
  } catch (error) {
    console.error('[WhiteLabel] Failed to fetch all configs:', error);
    return [];
  }
}

// Override the current domain config with a specific config (for dev testing)
const OVERRIDE_KEY = 'whitelabel_override_domain';

export function setWhiteLabelOverride(domain: string | null): void {
  if (domain) {
    localStorage.setItem(OVERRIDE_KEY, domain);
  } else {
    localStorage.removeItem(OVERRIDE_KEY);
    // Reset all white-label CSS variables to defaults when clearing
    resetWhiteLabelStyles();
  }
  // Clear cached config to force reload
  localStorage.removeItem(CONFIG_CACHE_KEY);
}

function resetWhiteLabelStyles(): void {
  console.log('[WhiteLabel] Resetting all white-label styles to defaults');
  
  const root = document.documentElement;
  
  // Remove all white-label CSS variables (they'll fall back to defaults in global.css)
  root.style.removeProperty('--primary');
  root.style.removeProperty('--primary-foreground');
  root.style.removeProperty('--secondary');
  root.style.removeProperty('--accent');
  root.style.removeProperty('--sidebar');
  root.style.removeProperty('--header-bar-color');
  root.style.removeProperty('--nav-bar-background');
  root.style.removeProperty('--nav-bar-text');
  root.style.removeProperty('--nav-bar-border');
  root.style.removeProperty('--accent-bar');
  
  // Remove custom CSS if any
  const customStyle = document.getElementById('whitelabel-custom-css');
  if (customStyle) {
    customStyle.remove();
  }
  
  // Reset favicon to default
  const existingFavicons = document.querySelectorAll('link[rel*="icon"]');
  existingFavicons.forEach(el => el.remove());
  
  // Reset document title
  const currentTitle = document.title;
  // This is a simple reset - you may want to restore to a specific default
  if (!currentTitle.includes('de.iterate')) {
    document.title = 'de.iterate';
  }
}

export function getWhiteLabelOverride(): string | null {
  return localStorage.getItem(OVERRIDE_KEY);
}

export async function fetchWhiteLabelConfigWithOverride(): Promise<WhiteLabelConfig | null> {
  const override = getWhiteLabelOverride();
  if (override) {
    console.log('[WhiteLabel] Using override domain:', override);
    return fetchWhiteLabelConfig(override);
  }
  return fetchWhiteLabelConfig();
}
